import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Disclaimer from "@/components/Disclaimer";
import Seo from "@/components/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";
import {
  ArrowRight,
  Brain,
  ShieldCheck,
  Zap,
  BarChart3,
  Trophy,
  Layers,
  Lock,
  Sparkles,
  CheckCircle2,
  LineChart,
  Eye,
  Clock,
  Database,
  Gauge,
} from "lucide-react";

const SITE_URL = "https://aleaedge.com";

const FAQ = [
  {
    q: "What is AI sports betting analysis?",
    a: "AI sports betting analysis combines historical data, team and player performance, market line movement and matchup context to grade each potential play. AleaEdge runs every candidate through a 100-point confidence model, then surfaces only the highest-conviction plays after a human admin review.",
  },
  {
    q: "How does AleaEdge calculate confidence?",
    a: "Each pick is scored across seven weighted signals: line value (25), recent form (15), matchup edge (15), injury impact (15), market movement (10), historical hit rate (10) and risk adjustment (10). The total confidence score (0–100) maps directly to a 1–5 unit rating.",
  },
  {
    q: "How is Edge % calculated?",
    a: "Edge % compares our projected probability for an outcome against the sportsbook's implied probability on the best available price across major US books. The consensus probability is averaged across multiple sportsbooks before we calculate the gap. It's a real, market-anchored number — not a marketing figure.",
  },
  {
    q: "Why are AleaEdge edges lower than other betting apps?",
    a: "Most apps inflate edge percentages because it looks impressive in marketing. AleaEdge uses conservative modeling tied to real sportsbook pricing discrepancies. A small but honest edge compounds over hundreds of plays — a fake 12% edge does not.",
  },
  {
    q: "Why does AleaEdge use conservative modeling?",
    a: "Because we're built for long-term bettors, not screenshots. Conservative edge calculations protect users from over-sizing plays based on inflated EV. We'd rather underpromise and let the public track record speak.",
  },
  {
    q: "What makes AleaEdge different?",
    a: "Transparent AI-assisted sportsbook analysis, conservative probability modeling, real market discrepancies, a fully tracked public history and a refusal to inflate numbers. No hype, no fairy tales — just disciplined betting intelligence.",
  },
  {
    q: "Are picks updated daily?",
    a: "Yes. The Top 5 board, All Picks board and Parlay of the Day refresh every day before game time, anchored to the Pacific Time betting day (3 AM PT rollover) so the cutover stays consistent for every user.",
  },
  {
    q: "Is performance tracked publicly?",
    a: "Every published pick is permanently tracked. Once a game settles, the result, units won/lost and ROI feed directly into the public record on the Dashboard and History pages — nothing is hidden, edited or removed after the fact.",
  },
  {
    q: "Can I buy one parlay without subscribing?",
    a: "Yes. The Parlay of the Day is available as a one-time $2.99 purchase if you don't want a full subscription. Pro members get every Parlay of the Day automatically unlocked.",
  },
  {
    q: "How does Parlay of the Day work?",
    a: "We curate a single multi-leg parlay each day, with correlation notes, suggested unit size and a written breakdown of why the legs work together. Free users can preview it; Pro and one-time buyers see every leg.",
  },
  {
    q: "What sports are covered?",
    a: "AleaEdge generates AI-graded picks across MLB, NBA, NFL, NHL and major Soccer leagues. Coverage scales with each league's active season.",
  },
  {
    q: "Do you guarantee wins?",
    a: "No. Sports betting is variance-driven and no service can guarantee outcomes. AleaEdge provides transparent, AI-assisted analysis and a public track record — bet sizing and decisions are always yours.",
  },
  {
    q: "What sportsbooks are supported?",
    a: "Picks reference odds from major US sportsbooks (DraftKings, FanDuel, BetMGM, Caesars and others) so you can shop the best line at the book of your choice.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Pro is month-to-month and can be cancelled at any time from the Account page. Access continues through the end of your current billing period.",
  },
];

const SPORTS = [
  {
    code: "MLB",
    icon: "⚾",
    title: "MLB AI Picks",
    body:
      "Daily AI-graded MLB picks across moneylines, run lines, totals and player props. Our model weighs starting pitcher matchups, bullpen workload, ballpark factors, weather and lineup splits to surface the best baseball value of the day.",
    cta: "See today's MLB plays",
    color: "from-blue-500/20 to-blue-500/5",
  },
  {
    code: "NBA",
    icon: "🏀",
    title: "NBA AI Predictions",
    body:
      "Spreads, totals and player props for the full NBA slate. AleaEdge tracks pace, usage rate, defensive matchups, injury reports and live line movement to identify spots where the closing market disagrees with the model.",
    cta: "View NBA Top 5",
    color: "from-orange-500/20 to-orange-500/5",
  },
  {
    code: "NHL",
    icon: "🏒",
    title: "NHL Betting Insights",
    body:
      "Puck lines, totals and goalie-driven props. Our NHL model emphasizes goaltender confirmation, expected goals, special teams and rest advantage — the variables that actually move hockey lines.",
    cta: "Browse NHL picks",
    color: "from-cyan-500/20 to-cyan-500/5",
  },
  {
    code: "Soccer",
    icon: "⚽",
    title: "Soccer Betting Analysis",
    body:
      "AI-driven analysis across the Premier League, La Liga, Serie A, Bundesliga, Ligue 1, MLS and Champions League. Match outcomes, Asian handicaps, totals and BTTS are graded against expected-goals and lineup data.",
    cta: "Explore soccer plays",
    color: "from-emerald-500/20 to-emerald-500/5",
  },
];

const STEPS = [
  {
    icon: Database,
    title: "1. Data ingestion",
    body:
      "We pull live odds, line history, injury reports and matchup data across MLB, NBA, NFL, NHL and Soccer markets.",
  },
  {
    icon: Brain,
    title: "2. AI grading",
    body:
      "Every candidate play is scored on 7 weighted signals — line value, form, matchup, injuries, market move, hit rate and risk.",
  },
  {
    icon: Gauge,
    title: "3. Confidence model",
    body:
      "The 100-point score maps to a 1–5 unit rating, so you always know how much conviction sits behind a pick.",
  },
  {
    icon: ShieldCheck,
    title: "4. Human review",
    body:
      "An admin reviews every play before publish — no auto-published picks, no inflated records, no after-the-fact edits.",
  },
  {
    icon: LineChart,
    title: "5. Tracking & ROI",
    body:
      "Once a game settles, results flow into the public ledger. Wins, losses, pushes and ROI are auditable on the History page.",
  },
];

type RecentPick = {
  id: string;
  title: string;
  result: string;
  units_delta: number;
  published_at: string | null;
  sports: { code: string } | null;
};

export default function Landing() {
  const [recent, setRecent] = useState<RecentPick[]>([]);

  useEffect(() => {
    // Inject FAQ + Organization + WebSite JSON-LD
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "AleaEdge",
        url: SITE_URL,
        logo: `${SITE_URL}/placeholder.svg`,
        sameAs: [],
        description:
          "AI-powered sports picks and betting insights across MLB, NBA, NFL, NHL and Soccer.",
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "AleaEdge",
        url: SITE_URL,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/picks?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    ld.id = "ld-home";
    document.head.appendChild(ld);
    return () => {
      document.getElementById("ld-home")?.remove();
    };
  }, []);

  useEffect(() => {
    // Best-effort: only returns rows for authenticated users due to RLS.
    (async () => {
      const { data } = await supabase
        .from("picks")
        .select("id, title, result, units_delta, published_at, sports(code)")
        .eq("status", "published")
        .is("deleted_at", null)
        .in("result", ["won", "lost", "pushed"])
        .order("published_at", { ascending: false })
        .limit(6);
      if (data) setRecent(data as unknown as RecentPick[]);
    })();
  }, []);

  return (
    <div>
      <Seo
        title="AleaEdge | AI Sports Picks, Predictions & Betting Insights"
        description="Daily AI-powered sports picks across MLB, NBA, NFL, NHL and Soccer. Transparent records, full breakdowns and premium parlays."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "AleaEdge",
          url: "https://aleaedge.com/",
        }}
      />
      {/* HERO */}
      <section className="container pt-12 md:pt-24 pb-12 md:pb-20">
        <div className="max-w-3xl mx-auto text-center">
          <span className="te-chip border border-primary/30 bg-primary/10 text-primary mb-5 md:mb-6">
            <Zap className="w-3 h-3" /> AI Sports Intelligence · Updated daily
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight">
            AI Sports Picks.<br className="hidden sm:block" />{" "}
            <span className="te-grad-text">Tracked. Transparent. Daily.</span>
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            A 100-point confidence engine ranks the best daily plays across MLB, NBA, NFL,
            NHL and Soccer. Every pick is human-reviewed, every result is public.
          </p>
          <div className="mt-7 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/auth" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-glow"
              >
                Get today's free pick <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing" className="w-full sm:w-auto">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                See Pro pricing
              </Button>
            </Link>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Free pick daily
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> No credit card
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> Human reviewed
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-primary" /> Updated before game time
            </span>
          </div>
        </div>

        {/* FEATURE GRID */}
        <div className="mt-14 md:mt-20 grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {features.map((f) => (
            <article
              key={f.title}
              className="te-card p-6 hover:shadow-elevated transition-shadow duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-primary grid place-items-center text-primary-foreground mb-4 shadow-glow">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-lg">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{f.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS — long-form SEO content */}
      <section className="container py-12 md:py-20 border-t border-border/40">
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-14">
          <span className="te-chip bg-primary/15 text-primary border border-primary/30 mb-3">
            <Brain className="w-3 h-3" /> How AleaEdge works
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold">
            From raw data to a tracked, human-reviewed pick
          </h2>
          <p className="text-muted-foreground mt-3 md:mt-4 text-base md:text-lg">
            AleaEdge isn't a tipster service. It's a sports intelligence pipeline that
            grades every candidate play with the same 100-point model — across MLB, NBA,
            NFL, NHL and Soccer — and only publishes what survives review.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-3 md:gap-4">
          {STEPS.map((s) => (
            <div key={s.title} className="te-card p-5 md:p-6">
              <div className="w-10 h-10 rounded-xl bg-secondary border border-border grid place-items-center mb-3">
                <s.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display font-bold text-base">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 md:mt-12 te-card p-6 md:p-10 max-w-4xl mx-auto">
          <h3 className="font-display text-xl md:text-2xl font-bold mb-3">
            The 100-point confidence model in plain English
          </h3>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            Every play starts with a search for <strong className="text-foreground">closing-line value</strong> —
            the difference between the price we'd take and the price the sharp market is
            settling at. From there the model layers in <strong className="text-foreground">recent form</strong>,
            <strong className="text-foreground"> matchup edge</strong> (pace, splits, defensive rating, expected goals,
            pitcher vs. lineup), <strong className="text-foreground">injury impact</strong> with confirmed inactives,
            and <strong className="text-foreground">market movement</strong> from open to current. We weight
            <strong className="text-foreground"> historical hit rate</strong> for the bet type and finally apply a
            <strong className="text-foreground"> risk adjustment</strong> based on volatility and correlation. The
            7-signal score (0–100) translates into a 1–5 unit rating, so you can size every
            play to your own bankroll with conviction grounded in math, not vibes.
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 text-sm">
            {scoreSignals.map((s) => (
              <div
                key={s.label}
                className="rounded-xl bg-secondary/60 border border-border/50 p-3 text-center"
              >
                <div className="font-display text-2xl te-grad-text font-bold">{s.pts}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPORT-SPECIFIC SEO SECTIONS */}
      <section className="container py-12 md:py-20 border-t border-border/40">
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-14">
          <span className="te-chip bg-accent/15 text-accent border border-accent/30 mb-3">
            <Trophy className="w-3 h-3" /> Coverage
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold">
            Daily AI plays across every major league
          </h2>
          <p className="text-muted-foreground mt-3 md:mt-4 text-base md:text-lg">
            One model, every sport in season. Topical depth from baseball to the
            Premier League.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-5">
          {SPORTS.map((s) => (
            <article
              key={s.code}
              className={`te-card p-6 md:p-7 bg-gradient-to-br ${s.color} hover:shadow-elevated transition-shadow`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl" aria-hidden>{s.icon}</span>
                <h3 className="font-display text-xl md:text-2xl font-bold">{s.title}</h3>
              </div>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {s.body}
              </p>
              <Link
                to="/picks"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                {s.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* TRANSPARENCY / EDGE METHODOLOGY */}
      <section className="container py-12 md:py-20 border-t border-border/40">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <span className="te-chip bg-accent/15 text-accent border border-accent/30 mb-3">
            <Gauge className="w-3 h-3" /> Transparent edge modeling
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold">
            Built for serious bettors, <span className="te-grad-text">not fake hype</span>
          </h2>
          <p className="text-muted-foreground mt-3 md:mt-4 text-base md:text-lg">
            Many betting apps inflate edge percentages because it looks impressive in marketing.
            AleaEdge uses conservative edge calculations based on real sportsbook pricing
            discrepancies and AI-assisted market analysis. We prioritize long-term transparency
            over inflated projections.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 max-w-5xl mx-auto">
          {[
            {
              icon: Database,
              title: "Real sportsbook data",
              body: "Edge is measured against live odds across major US books — not back-fit numbers.",
            },
            {
              icon: Gauge,
              title: "Conservative probability modeling",
              body: "Consensus probabilities are averaged across books before edge is calculated.",
            },
            {
              icon: ShieldCheck,
              title: "No exaggerated projections",
              body: "Small honest edges compound. We won't fake a 12% EV to win a screenshot war.",
            },
            {
              icon: Brain,
              title: "AI-assisted, market-anchored",
              body: "Models surface candidates; pricing across the market keeps the math grounded.",
            },
            {
              icon: BarChart3,
              title: "Long-term value focus",
              body: "Built for bettors who care about variance, ROI and discipline — not hype days.",
            },
            {
              icon: Eye,
              title: "Auditable history",
              body: "Every published pick stays in the public ledger — wins, losses and pushes.",
            },
          ].map((t) => (
            <div key={t.title} className="te-card p-5 flex gap-3">
              <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary/10 text-primary shrink-0">
                <t.icon className="w-4 h-4" />
              </span>
              <div>
                <div className="font-semibold">{t.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{t.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST / DIFFERENTIATION */}
      <section className="container py-12 md:py-20 border-t border-border/40">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <span className="te-chip bg-primary/15 text-primary border border-primary/30 mb-3">
            <ShieldCheck className="w-3 h-3" /> Why AleaEdge
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold">
            What makes AleaEdge different
          </h2>
          <p className="text-muted-foreground mt-3 md:mt-4 text-base md:text-lg">
            No guaranteed wins. No fake records. Just transparent AI-assisted sports analysis.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 max-w-5xl mx-auto">
          {TRUST.map((t) => (
            <div key={t.title} className="te-card p-5 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">{t.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{t.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* RECENT RESULTS — only renders when data is accessible */}
      {recent.length > 0 && (
        <section className="container py-12 md:py-20 border-t border-border/40">
          <div className="flex items-end justify-between mb-6 md:mb-8 gap-4">
            <div>
              <span className="te-chip bg-primary/15 text-primary border border-primary/30 mb-2">
                <Clock className="w-3 h-3" /> Live
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold">Recent results</h2>
              <p className="text-muted-foreground mt-1 text-sm md:text-base">
                Every published pick is permanently tracked.
              </p>
            </div>
            <Link
              to="/history"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Full history <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {recent.map((p) => {
              const won = p.result === "won";
              const lost = p.result === "lost";
              return (
                <div key={p.id} className="te-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="te-chip bg-secondary border border-border text-xs">
                      {p.sports?.code ?? "PICK"}
                    </span>
                    <span
                      className={`te-chip text-xs ${
                        won
                          ? "bg-success/15 text-success border border-success/30"
                          : lost
                          ? "bg-danger/15 text-danger border border-danger/30"
                          : "bg-muted text-muted-foreground border border-border"
                      }`}
                    >
                      {p.result.toUpperCase()} · {Number(p.units_delta) > 0 ? "+" : ""}
                      {Number(p.units_delta).toFixed(2)}u
                    </span>
                  </div>
                  <div className="font-medium text-sm line-clamp-2">{p.title}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* FREE vs PRO */}
      <section className="container py-12 md:py-20 border-t border-border/40">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="font-display text-3xl md:text-5xl font-bold">
            Free vs <span className="te-grad-text">Pro</span>
          </h2>
          <p className="text-muted-foreground mt-2">Start free. Upgrade when you want every edge.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 max-w-4xl mx-auto">
          <div className="te-card p-6">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-display text-xl font-bold">Free</h3>
            </div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {["1 free pick per day", "Basic AI summary", "Pick history snapshot"].map((t) => (
                <li key={t} className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="te-card p-6 ring-2 ring-primary/60 shadow-elevated relative">
            <span className="te-chip bg-gradient-primary text-primary-foreground absolute -top-3 right-5">
              Most popular
            </span>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="font-display text-xl font-bold">Pro</h3>
            </div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {[
                "All Top 5 picks",
                "Full pick breakdowns",
                "All Picks board",
                "Parlay of the Day",
                "Full history & stats",
              ].map((t) => (
                <li key={t} className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/pricing">
              <Button className="w-full mt-5 bg-gradient-accent text-accent-foreground hover:opacity-90">
                <Sparkles className="w-4 h-4 mr-2" /> See Pro pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container py-12 md:py-20 border-t border-border/40">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="te-chip bg-secondary border border-border mb-3">
              <Eye className="w-3 h-3" /> Frequently asked
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold">Questions, answered</h2>
            <p className="text-muted-foreground mt-3">
              Everything you'd want to know about how AleaEdge generates and tracks picks.
            </p>
          </div>
          <div className="te-card p-2 md:p-4">
            <Accordion type="single" collapsible className="w-full">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="border-border/60">
                  <AccordionTrigger className="px-3 md:px-4 text-left text-base md:text-lg font-semibold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* INTERNAL LINK HUB */}
      <section className="container py-12 md:py-16 border-t border-border/40">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-6 text-center">
            Explore AleaEdge
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { to: "/top-5", label: "Top 5 board" },
              { to: "/picks", label: "All picks" },
              { to: "/history", label: "Tracked history" },
              { to: "/pricing", label: "Pro pricing" },
              { to: "/auth", label: "Create account" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="te-card p-4 text-center text-sm font-medium hover:border-primary/50 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container pb-16 md:pb-24">
        <div className="te-card p-6 md:p-12 text-center bg-gradient-card max-w-4xl mx-auto">
          <Trophy className="w-8 h-8 text-accent mx-auto mb-3" />
          <h2 className="font-display text-2xl md:text-4xl font-bold">
            Ready to see today's plays?
          </h2>
          <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
            Create a free account and unlock today's AI-graded free pick instantly. No
            credit card required.
          </p>
          <Link to="/auth">
            <Button
              size="lg"
              className="mt-5 bg-gradient-primary text-primary-foreground shadow-glow"
            >
              Unlock today's free pick <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-4">
            Daily AI-generated sports insights · Every published pick is permanently tracked
          </p>
        </div>

        <div className="mt-12 md:mt-16 max-w-3xl mx-auto">
          <Disclaimer />
        </div>
      </section>
    </div>
  );
}

const features = [
  {
    icon: Brain,
    title: "AI-graded picks",
    desc: "A 100-point confidence model converts seven weighted signals into a clean 1–5 unit rating.",
  },
  {
    icon: ShieldCheck,
    title: "Admin reviewed",
    desc: "No pick is auto-published. Every play is approved by a human before it reaches you.",
  },
  {
    icon: BarChart3,
    title: "Tracked results",
    desc: "Every settled pick contributes to a public record, units and ROI you can audit.",
  },
  {
    icon: Layers,
    title: "Parlay of the Day",
    desc: "A daily curated parlay with correlation notes — Pro members get every leg unlocked.",
  },
  {
    icon: Trophy,
    title: "Top 5 board",
    desc: "The five highest-confidence plays of the day, ranked side-by-side.",
  },
  {
    icon: Zap,
    title: "Live odds context",
    desc: "Line movement and sportsbook context built into every breakdown.",
  },
];

const scoreSignals = [
  { pts: 25, label: "Line value" },
  { pts: 15, label: "Recent form" },
  { pts: 15, label: "Matchup edge" },
  { pts: 15, label: "Injuries" },
  { pts: 10, label: "Market move" },
  { pts: 10, label: "Hit rate" },
  { pts: 10, label: "Risk adj" },
];

const TRUST = [
  {
    title: "Transparent tracked results",
    body: "Every published pick is permanently logged with result, units and ROI.",
  },
  {
    title: "AI-assisted, not AI-only",
    body: "Models surface candidates; a human admin approves every published play.",
  },
  {
    title: "Daily updated insights",
    body: "Top 5, All Picks and Parlay of the Day refresh before game time, every day.",
  },
  {
    title: "Public history & ROI",
    body: "Browse the full ledger on the History page — wins, losses and pushes included.",
  },
  {
    title: "No guaranteed wins",
    body: "Sports betting is variance-driven. We give you the math, not fairy tales.",
  },
  {
    title: "Built for bankroll discipline",
    body: "1–5 unit ratings let you scale every play to your own risk tolerance.",
  },
];
